import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import {
  HiChartBar,
  HiHeart,
  HiClipboardDocumentList,
  HiUsers,
  HiArrowTrendingUp,
  HiDocumentText,
} from 'react-icons/hi2';
import DiagnosticChecklist from './DiagnosticChecklist';
import SmartInsight from './SmartInsight';
import TrendGraph from './TrendGraph';
import BusinessHealthAssessmentForm from './BusinessHealthAssessmentForm';
import HealthOverviewSection from './HealthOverviewSection';
import {
  mergeHealthDataFromAssessment,
  mapAssessmentToHealthCardScores,
  appendTrendSnapshot,
  getDefaultDiagnosticDomains,
  isDiagnosticChecklistEmpty,
} from './healthCheckHelpers';
import { firestore, storage } from '../../database/firebase';
import { collection, doc, getDoc, setDoc, onSnapshot, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import useCurrentUser from '../../hooks/useCurrentUser';
import { MarbleLoader } from '../ui/LoadingStates';

const CHECKLISTS_COLLECTION = 'checklists';
const HEALTHSCORES_COLLECTION = 'healthScores';
const SUBMISSIONS_COLLECTION = 'healthSubmissions';

const HealthDashboard = ({ userRole: propUserRole, userId: propUserId }) => {
  const { user: currentUser, loading: userLoading } = useCurrentUser();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checklistData, setChecklistData] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [submissionsError, setSubmissionsError] = useState(null);
  const [expandedSubmission, setExpandedSubmission] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [assessmentData, setAssessmentData] = useState(null);
  const [allUsersData, setAllUsersData] = useState([]);
  const [analyticsData, setAnalyticsData] = useState({});
  const checklistInitRef = useRef(false);
  const loadGateRef = useRef(0);

  const userId = propUserId || currentUser?.id || currentUser?.uid;
  const userRole = propUserRole || currentUser?.role;

  const canViewAllData = userRole === 'admin' || userRole === 'moderator' || userRole === 'coach';

  const canEditChecklist = userRole === 'admin' || userRole === 'coach' || userRole === 'client';

  useEffect(() => {
    if (!userId || !userRole) return;
    setLoading(true);
    setError(null);
    loadGateRef.current = 0;

    const bumpReady = () => {
      loadGateRef.current += 1;
      if (loadGateRef.current >= 2) setLoading(false);
    };

    const checklistRef = doc(firestore, CHECKLISTS_COLLECTION, userId);
    const unsubChecklist = onSnapshot(
      checklistRef,
      docSnap => {
        if (docSnap.exists()) {
          setChecklistData(docSnap.data().domains || {});
        } else {
          setChecklistData({});
        }
        bumpReady();
      },
      err => {
        setError('Failed to load checklist data');
        setChecklistData({});
        bumpReady();
      }
    );

    const healthRef = doc(firestore, HEALTHSCORES_COLLECTION, userId);
    const unsubHealth = onSnapshot(
      healthRef,
      docSnap => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setHealthData(data.healthData || {});
          setTrendData(data.trendData || {});
        } else {
          setHealthData({});
          setTrendData({});
        }
        bumpReady();
      },
      err => {
        setError('Failed to load health score data');
        setHealthData({});
        setTrendData({});
        bumpReady();
      }
    );

    return () => {
      unsubChecklist();
      unsubHealth();
    };
  }, [userId, userRole]);

  useEffect(() => {
    if (checklistData === null) return;
    if (!userId || !canEditChecklist || checklistInitRef.current) return;
    if (!isDiagnosticChecklistEmpty(checklistData)) return;

    checklistInitRef.current = true;
    (async () => {
      try {
        const defaults = getDefaultDiagnosticDomains();
        await setDoc(
          doc(firestore, CHECKLISTS_COLLECTION, userId),
          {
            domains: defaults,
            meta: {
              initializedAt: new Date().toISOString(),
              version: 1,
            },
          },
          { merge: true }
        );
        toast.info('Diagnostic checklist initialized with recommended items.');
      } catch (e) {
        checklistInitRef.current = false;
      }
    })();
  }, [userId, canEditChecklist, checklistData]);

  useEffect(() => {
    if (!canViewAllData) return;
    setSubmissionsLoading(true);
    setSubmissionsError(null);

    const submissionsRef = collection(firestore, SUBMISSIONS_COLLECTION);
    const unsub = onSnapshot(
      submissionsRef,
      snapshot => {
        const submissionsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setSubmissions(submissionsData);

        if (submissionsData.length > 0) {
          const analytics = calculateAnalytics(submissionsData);
          setAnalyticsData(analytics);
        }

        setSubmissionsLoading(false);
      },
      err => {
        setSubmissionsError('Failed to load submissions');
        setSubmissionsLoading(false);
      }
    );
    return () => unsub();
  }, [canViewAllData]);

  useEffect(() => {
    if (!canViewAllData) return;

    const fetchAllUsersData = async () => {
      try {
        const assessmentsRef = collection(firestore, 'businessHealthAssessments');
        const snapshot = await getDocs(assessmentsRef);
        const usersData = snapshot.docs.map(doc => ({
          userId: doc.id,
          ...doc.data(),
        }));
        setAllUsersData(usersData);
      } catch (error) {
        toast.error('Failed to fetch all users data');
      }
    };

    fetchAllUsersData();
  }, [canViewAllData]);

  useEffect(() => {
    if (!userId) return;

    const assessmentRef = doc(firestore, 'businessHealthAssessments', userId);
    const unsub = onSnapshot(
      assessmentRef,
      docSnap => {
        if (docSnap.exists()) {
          setAssessmentData(docSnap.data());
        }
      },
      err => {
        toast.error('Failed to load assessment data');
      }
    );

    return () => unsub();
  }, [userId]);

  const calculateAnalytics = submissions => {
    const totalSubmissions = submissions.length;
    const avgHealthScore =
      submissions.reduce(
        (sum, sub) => sum + (sub.healthData?.overallScore ?? sub.assessmentData?.overallScore ?? 0),
        0
      ) / (totalSubmissions || 1);
    const completionRate =
      (submissions.filter(sub => sub.checklistData && Object.keys(sub.checklistData).length > 0)
        .length /
        totalSubmissions) *
      100;

    return {
      totalSubmissions,
      avgHealthScore: Math.round(avgHealthScore),
      completionRate: Math.round(completionRate),
      recentSubmissions: submissions.slice(-5),
    };
  };

  const canEdit = userRole === 'admin' || userRole === 'coach' || userRole === 'client';
  const canAdd = userRole === 'admin' || userRole === 'coach';
  const canDelete = userRole === 'admin';

  const checklistMetaPatch = () => ({
    meta: { updatedAt: new Date().toISOString() },
  });

  const displayHealthData = useMemo(
    () => mergeHealthDataFromAssessment(healthData || {}, assessmentData),
    [healthData, assessmentData]
  );

  const handleChecklistUpdate = async newData => {
    if (!canEdit) {
      toast.error('You do not have permission to update the checklist.');
      return;
    }
    try {
      await setDoc(
        doc(firestore, CHECKLISTS_COLLECTION, userId),
        { domains: newData, ...checklistMetaPatch() },
        { merge: true }
      );
      toast.success('Checklist updated!');
    } catch (err) {
      toast.error('Failed to update checklist.');
    }
  };

  const handleAddItem = async (category, item) => {
    if (!canAdd) {
      toast.error('You do not have permission to add items.');
      return;
    }
    try {
      const docRef = doc(firestore, CHECKLISTS_COLLECTION, userId);
      const docSnap = await getDoc(docRef);
      const prev = docSnap.exists() ? docSnap.data().domains || {} : {};
      const updated = {
        ...prev,
        [category]: [...(prev[category] || []), item],
      };
      await setDoc(docRef, { domains: updated, ...checklistMetaPatch() }, { merge: true });
      toast.success('Checklist item added!');
    } catch (err) {
      toast.error('Failed to add item.');
    }
  };

  const handleEditItem = async (category, itemId, updatedData) => {
    if (!canEdit) {
      toast.error('You do not have permission to edit items.');
      return;
    }
    try {
      const docRef = doc(firestore, CHECKLISTS_COLLECTION, userId);
      const docSnap = await getDoc(docRef);
      const prev = docSnap.exists() ? docSnap.data().domains || {} : {};
      const updated = {
        ...prev,
        [category]: (prev[category] || []).map(item =>
          String(item.id) === String(itemId) ? { ...item, ...updatedData } : item
        ),
      };
      await setDoc(docRef, { domains: updated, ...checklistMetaPatch() }, { merge: true });
      toast.success('Checklist item updated!');
    } catch (err) {
      toast.error('Failed to update item.');
    }
  };

  const handleDeleteItem = async (category, itemId) => {
    if (!canDelete) {
      toast.error('You do not have permission to delete items.');
      return;
    }
    try {
      const docRef = doc(firestore, CHECKLISTS_COLLECTION, userId);
      const docSnap = await getDoc(docRef);
      const prev = docSnap.exists() ? docSnap.data().domains || {} : {};
      const updated = {
        ...prev,
        [category]: (prev[category] || []).filter(item => String(item.id) !== String(itemId)),
      };
      await setDoc(docRef, { domains: updated, ...checklistMetaPatch() }, { merge: true });
      toast.success('Checklist item deleted!');
    } catch (err) {
      toast.error('Failed to delete item.');
    }
  };

  const handleFileUpload = async (file, checklistCategory, itemId) => {
    setUploading(true);
    try {
      const storagePath = `${userId}/${checklistCategory}/${itemId}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      await handleEditItem(checklistCategory, itemId, { attachment: url });
      toast.success('File uploaded!');
      setUploading(false);
      return url;
    } catch (err) {
      setUploading(false);
      toast.error('File upload failed.');
      return null;
    }
  };

  const handleSubmitHealth = async () => {
    if (!userId) {
      toast.error('Cannot submit: not signed in.');
      return;
    }
    if (!checklistData && !assessmentData) {
      toast.error('Complete at least the checklist or health assessment before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      await setDoc(doc(collection(firestore, SUBMISSIONS_COLLECTION), userId), {
        userId,
        userEmail: currentUser?.email || '',
        submittedAt: new Date().toISOString(),
        checklistData: checklistData || {},
        healthData: healthData || {},
        trendData: trendData || {},
        assessmentData: assessmentData || null,
      });
      toast.success('Health package submitted!');
    } catch (err) {
      toast.error('Failed to submit health assessment.');
    }
    setSubmitting(false);
  };

  const handleSaveAssessment = async data => {
    try {
      await setDoc(doc(firestore, 'businessHealthAssessments', userId), data, { merge: true });

      setAssessmentData(data);

      const cardMapped = mapAssessmentToHealthCardScores(data.categoryScores);
      const updatedHealthData = mergeHealthDataFromAssessment(healthData || {}, data);
      setHealthData(updatedHealthData);

      const newTrend = appendTrendSnapshot(trendData || {}, {
        overall: data.overallScore,
        finance: cardMapped.finance,
        sales: cardMapped.sales,
        operations: cardMapped.operations,
        hr: cardMapped.hr,
        compliance: cardMapped.compliance,
      });
      setTrendData(newTrend);

      await setDoc(
        doc(firestore, HEALTHSCORES_COLLECTION, userId),
        {
          healthData: updatedHealthData,
          trendData: newTrend,
          lastAssessmentSync: data.lastUpdated,
        },
        { merge: true }
      );

      toast.success('Business health assessment saved successfully!');
    } catch (error) {
      toast.error('Failed to save assessment');

      throw error;
    }
  };

  if (userLoading || loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <MarbleLoader size="md" />
      </div>
    );
  }
  if (error) {
    return <div className="flex h-96 items-center justify-center text-red-600">{error}</div>;
  }

  const checklistProps = {
    checklistData: checklistData || {},
    userRole,
    onAddItem: handleAddItem,
    onEditItem: handleEditItem,
    onDeleteItem: handleDeleteItem,
    onFileUpload: handleFileUpload,
    uploading,
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4">
      {canViewAllData && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-4"
        >
          <div className="rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100 p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">Total Submissions</p>
                <p className="text-2xl font-bold text-blue-900">
                  {analyticsData.totalSubmissions || 0}
                </p>
              </div>
              <HiDocumentText className="h-8 w-8 text-blue-600" />
            </div>
          </div>

          <div className="rounded-xl border border-green-200 bg-gradient-to-br from-green-50 to-green-100 p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Avg Health Score</p>
                <p className="text-2xl font-bold text-green-900">
                  {analyticsData.avgHealthScore || 0}%
                </p>
              </div>
              <HiArrowTrendingUp className="h-8 w-8 text-green-600" />
            </div>
          </div>

          <div className="rounded-xl border border-purple-200 bg-gradient-to-br from-purple-50 to-purple-100 p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600">Completion Rate</p>
                <p className="text-2xl font-bold text-purple-900">
                  {analyticsData.completionRate || 0}%
                </p>
              </div>
              <HiUsers className="h-8 w-8 text-purple-600" />
            </div>
          </div>

          <div className="rounded-xl border border-orange-200 bg-gradient-to-br from-orange-50 to-orange-100 p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600">Active Users</p>
                <p className="text-2xl font-bold text-orange-900">{allUsersData.length}</p>
              </div>
              <HiUsers className="h-8 w-8 text-orange-600" />
            </div>
          </div>
        </motion.div>
      )}

      {canViewAllData && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:p-6"
        >
          <h2 className="mb-4 text-xl font-bold text-gray-900">All Health Submissions</h2>
          {submissionsLoading ? (
            <div className="text-gray-700">Loading submissions...</div>
          ) : submissionsError ? (
            <div className="text-red-600">{submissionsError}</div>
          ) : submissions.length === 0 ? (
            <div className="text-gray-600">No submissions yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Email</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">
                      Health Score
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">
                      Submitted At
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map(sub => {
                    const scoreRow =
                      sub.healthData?.overallScore ?? sub.assessmentData?.overallScore ?? 0;
                    return (
                      <React.Fragment key={sub.id}>
                        <tr className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-3 text-gray-700">{sub.userEmail}</td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                scoreRow >= 80
                                  ? 'bg-green-100 text-green-800'
                                  : scoreRow >= 60
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {scoreRow}%
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-700">
                            {new Date(sub.submittedAt).toLocaleString()}
                          </td>
                          <td className="px-4 py-3">
                            <button
                              className="rounded-lg bg-blue-600 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-blue-700"
                              onClick={() =>
                                setExpandedSubmission(expandedSubmission === sub.id ? null : sub.id)
                              }
                            >
                              {expandedSubmission === sub.id ? 'Hide' : 'View Details'}
                            </button>
                          </td>
                        </tr>
                        {expandedSubmission === sub.id && (
                          <tr className="bg-gray-50">
                            <td colSpan={5} className="px-4 py-4">
                              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                                <div className="rounded-lg bg-white p-4 shadow-sm">
                                  <h4 className="mb-2 font-semibold text-gray-900">
                                    Checklist Data
                                  </h4>
                                  <pre className="max-h-40 overflow-y-auto rounded bg-gray-100 p-2 text-xs">
                                    {JSON.stringify(sub.checklistData, null, 2)}
                                  </pre>
                                </div>
                                <div className="rounded-lg bg-white p-4 shadow-sm">
                                  <h4 className="mb-2 font-semibold text-gray-900">Health Data</h4>
                                  <pre className="max-h-40 overflow-y-auto rounded bg-gray-100 p-2 text-xs">
                                    {JSON.stringify(sub.healthData, null, 2)}
                                  </pre>
                                </div>
                                <div className="rounded-lg bg-white p-4 shadow-sm">
                                  <h4 className="mb-2 font-semibold text-gray-900">Trend Data</h4>
                                  <pre className="max-h-40 overflow-y-auto rounded bg-gray-100 p-2 text-xs">
                                    {JSON.stringify(sub.trendData, null, 2)}
                                  </pre>
                                </div>
                                <div className="rounded-lg bg-white p-4 shadow-sm">
                                  <h4 className="mb-2 font-semibold text-gray-900">
                                    Assessment snapshot
                                  </h4>
                                  <pre className="max-h-40 overflow-y-auto rounded bg-gray-100 p-2 text-xs">
                                    {JSON.stringify(
                                      sub.assessmentData
                                        ? {
                                            overallScore: sub.assessmentData.overallScore,
                                            categoryScores: sub.assessmentData.categoryScores,
                                            lastUpdated: sub.assessmentData.lastUpdated,
                                          }
                                        : null,
                                      null,
                                      2
                                    )}
                                  </pre>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-xl border border-gray-200 bg-gradient-to-r from-white to-gray-50 p-6 shadow-sm"
      >
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Business Health Dashboard</h1>
            <p className="text-gray-600">
              Comprehensive health assessment based on your checklist completion and business
              metrics
            </p>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-600">Last Updated</div>
            <div className="text-base font-semibold text-gray-900">
              {assessmentData?.lastUpdated
                ? new Date(assessmentData.lastUpdated).toLocaleString()
                : displayHealthData?.lastUpdated
                  ? typeof displayHealthData.lastUpdated === 'string'
                    ? new Date(displayHealthData.lastUpdated).toLocaleString()
                    : String(displayHealthData.lastUpdated)
                  : '-'}
            </div>
          </div>
        </div>

        <div className="mt-6 border-b border-gray-200">
          <nav className="flex space-x-8">
            {[
              {
                id: 'overview',
                label: 'Overview',
                icon: <HiChartBar className="h-5 w-5" />,
                color: 'blue',
              },
              {
                id: 'assessment',
                label: 'Health Assessment',
                icon: <HiHeart className="h-5 w-5" />,
                color: 'red',
              },
              {
                id: 'checklist',
                label: 'Diagnostic Checklist',
                icon: <HiClipboardDocumentList className="h-5 w-5" />,
                color: 'green',
              },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition-all duration-200 ${
                  activeTab === tab.id
                    ? `border-${tab.color}-600 text-${tab.color}-600`
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </motion.div>

      <AnimatePresence mode="wait">
        {activeTab === 'overview' && (
          <motion.div
            key="overview"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            <HealthOverviewSection
              displayHealthData={displayHealthData}
              assessmentData={assessmentData}
              checklistData={checklistData || {}}
              onTabAssessment={() => setActiveTab('assessment')}
              onTabChecklist={() => setActiveTab('checklist')}
            />

            <TrendGraph
              trendData={trendData || {}}
              userRole={userRole}
              onTimeRangeChange={() => {}}
              onMetricToggle={() => {}}
              selectedMetrics={['overall', 'finance', 'sales', 'operations']}
              timeRange="6months"
            />

            <SmartInsight
              healthData={displayHealthData}
              trendData={trendData || {}}
              userRole={userRole}
              onInsightAction={() => setActiveTab('assessment')}
              onScheduleSession={() => {}}
              onViewResource={() => setActiveTab('checklist')}
            />
          </motion.div>
        )}

        {activeTab === 'assessment' && (
          <motion.div
            key="assessment"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <BusinessHealthAssessmentForm
              userRole={userRole}
              userId={userId}
              onSave={handleSaveAssessment}
              initialData={assessmentData?.formData}
              isReadOnly={userRole === 'admin' && userId !== currentUser?.uid}
            />
          </motion.div>
        )}

        {activeTab === 'checklist' && (
          <motion.div
            key="checklist"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <DiagnosticChecklist {...checklistProps} />
          </motion.div>
        )}
      </AnimatePresence>

      {userRole === 'client' && activeTab === 'overview' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="flex justify-end"
        >
          <button
            onClick={handleSubmitHealth}
            disabled={submitting}
            className="rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-3 font-semibold text-white transition-all duration-200 hover:from-blue-700 hover:to-blue-800 hover:shadow-lg disabled:opacity-60"
          >
            {submitting ? 'Submitting...' : 'Submit Health Assessment'}
          </button>
        </motion.div>
      )}
    </div>
  );
};

export default HealthDashboard;
